import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Mail, MailX, Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "./adminApi";
import MultiSelect from "./MultiSelect";

export default function AdminsTab() {
  const { user, checkAuth } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", assigned_agency_ids: [] });
  const [togglingMe, setTogglingMe] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [scopeEditor, setScopeEditor] = useState(null); // { admin, agency_ids }
  const [savingScope, setSavingScope] = useState(false);
  const fetchAdmins = useCallback(async () => { try { const r = await api.get("/admins"); setAdmins(r.data); } catch { /* noop */ } }, []);
  const fetchAgencies = useCallback(async () => { try { const r = await api.get("/agencies"); setAgencies(r.data); } catch { /* noop */ } }, []);
  useEffect(() => { fetchAdmins(); fetchAgencies(); }, [fetchAdmins, fetchAgencies]);

  const agencyOptions = [
    { key: "", label: "(Sans agence)" },
    ...agencies.map((a) => ({ key: a.agency_id, label: `${a.name}${a.city ? ` — ${a.city}` : ""}` })),
  ];
  const agencyLabel = (id) => (id === "" ? "Sans agence" : agencies.find((a) => a.agency_id === id)?.name || id);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Tous les champs sont requis");
      return;
    }
    try {
      await api.post("/admins", form);
      toast.success("Administrateur créé");
      setShowForm(false);
      setForm({ name: "", email: "", password: "", assigned_agency_ids: [] });
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const handleDelete = async (userId) => { if (!window.confirm("Supprimer cet administrateur ?")) return; try { await api.delete(`/admins/${userId}`); toast.success("Administrateur supprimé"); fetchAdmins(); } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } };

  const openScopeEditor = (admin) => {
    setScopeEditor({ admin, agency_ids: admin.assigned_agency_ids || [] });
  };
  const saveScope = async () => {
    if (!scopeEditor) return;
    setSavingScope(true);
    try {
      await api.patch(`/admins/${scopeEditor.admin.user_id}/agencies`, { agency_ids: scopeEditor.agency_ids });
      toast.success("Périmètre d'agences mis à jour");
      if (scopeEditor.admin.user_id === user?.user_id && checkAuth) await checkAuth();
      setScopeEditor(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSavingScope(false); }
  };

  const toggleMyEmailPref = async (next) => {
    setTogglingMe(true);
    try {
      await api.patch(`/admins/${user.user_id}/email-notifications`, { enabled: next });
      toast.success(next ? "Notifications email réactivées" : "Notifications email désactivées");
      if (checkAuth) await checkAuth();
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setTogglingMe(false);
    }
  };

  const toggleEmailPrefFor = async (admin, next) => {
    setTogglingId(admin.user_id);
    try {
      await api.patch(`/admins/${admin.user_id}/email-notifications`, { enabled: next });
      toast.success(`${admin.name} — emails ${next ? "activés" : "désactivés"}`);
      if (admin.user_id === user?.user_id && checkAuth) await checkAuth();
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setTogglingId(null);
    }
  };

  const myEmailPref = user?.email_notifications !== false;

  return (
    <div>
      {/* Préférence de notification de l'admin connecté */}
      <div className="bg-gradient-to-r from-[#F0E9F3] to-[#E6F0F7] border border-[#662483]/20 p-4 mb-5 flex items-center justify-between flex-wrap gap-3" data-testid="my-email-pref-block">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {myEmailPref ? <Mail className="w-5 h-5 text-[#662483] shrink-0 mt-0.5" /> : <MailX className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />}
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#3F3F3F]">Mes notifications par email</p>
            <p className="text-xs text-gray-600 mt-0.5">Recevoir un email à chaque <strong>nouvelle inscription candidat</strong> et chaque <strong>nouvelle candidature à une offre</strong>. Vous resterez visible dans les notifications temps réel du backoffice quoi qu&apos;il arrive.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {togglingMe && <Loader2 className="w-4 h-4 animate-spin text-[#662483]" />}
          <Switch
            checked={myEmailPref}
            onCheckedChange={toggleMyEmailPref}
            disabled={togglingMe}
            data-testid="toggle-my-email-pref"
          />
          <span className="text-xs font-semibold text-[#3F3F3F] w-12">{myEmailPref ? "Activé" : "Désactivé"}</span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{admins.length} administrateur(s)</p>
        <Button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="add-admin-btn"><Plus className="w-4 h-4 mr-2" /> Nouvel admin</Button>
      </div>
      <div className="space-y-3">
        {admins.map((a) => {
          const isMe = a.user_id === user?.user_id;
          const emailOn = a.email_notifications !== false;
          const isBusy = togglingId === a.user_id;
          const scope = a.assigned_agency_ids || [];
          const isSuper = scope.length === 0;
          return (
            <div key={a.user_id} className="bg-white border border-gray-200 p-4" data-testid={`admin-user-${a.user_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-bold text-[#3F3F3F]">{a.name} {isMe && <span className="text-xs text-[#662483] font-normal">(vous)</span>}</h3>
                  <p className="text-xs text-gray-500">{a.email}</p>
                </div>
                <div className="flex items-center gap-2" data-testid={`admin-email-pref-${a.user_id}`}>
                  {isBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#662483]" />}
                  {emailOn ? <Mail className="w-4 h-4 text-green-600" /> : <MailX className="w-4 h-4 text-gray-400" />}
                  <Switch
                    checked={emailOn}
                    onCheckedChange={(v) => toggleEmailPrefFor(a, v)}
                    disabled={isBusy}
                    data-testid={`toggle-email-pref-${a.user_id}`}
                  />
                  <span className="text-[10px] uppercase tracking-wider font-semibold w-16 text-right text-gray-500">{emailOn ? "Emails ON" : "Emails OFF"}</span>
                </div>
                <button
                  onClick={() => openScopeEditor(a)}
                  className="p-2 hover:bg-gray-100 text-[#0069B4]"
                  title="Gérer le périmètre d'agences"
                  data-testid={`edit-scope-${a.user_id}`}
                >
                  <Building2 className="w-4 h-4" />
                </button>
                {!isMe && <button onClick={() => handleDelete(a.user_id)} className="p-2 hover:bg-gray-100 text-red-500" data-testid={`delete-admin-${a.user_id}`}><Trash2 className="w-4 h-4" /></button>}
              </div>
              {/* Périmètre d'agences */}
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap" data-testid={`admin-scope-${a.user_id}`}>
                <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Périmètre</span>
                {isSuper ? (
                  <Badge className="bg-[#F0E9F3] text-[#662483] rounded-none text-[10px]">Super-admin (toutes agences)</Badge>
                ) : (
                  scope.map((sid) => (
                    <Badge key={sid || "__none__"} className="bg-[#E6F0F7] text-[#0069B4] rounded-none text-[10px]" data-testid={`scope-badge-${a.user_id}-${sid || "none"}`}>
                      {agencyLabel(sid)}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md rounded-none">
          <DialogHeader><DialogTitle className="text-[#3F3F3F]">Nouvel administrateur</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-sm font-semibold mb-1 block">Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="admin-form-name" /></div>
            <div><Label className="text-sm font-semibold mb-1 block">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" data-testid="admin-form-email" /></div>
            <div><Label className="text-sm font-semibold mb-1 block">Mot de passe *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none" data-testid="admin-form-password" /></div>
            <div>
              <Label className="text-sm font-semibold mb-1 block">Périmètre d&apos;agences</Label>
              <MultiSelect
                options={agencyOptions}
                value={form.assigned_agency_ids}
                onChange={(v) => setForm({ ...form, assigned_agency_ids: v })}
                placeholder="Toutes les agences (super-admin)"
                dataTestId="admin-form-scope"
              />
              <p className="text-[10px] text-gray-500 mt-1">Vide = super-admin (voit tout). Sinon l&apos;admin ne verra que les candidats/offres/candidatures des agences sélectionnées.</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleCreate} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="admin-form-save">Créer</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-none">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog édition du périmètre d'agences */}
      <Dialog open={!!scopeEditor} onOpenChange={(v) => !v && setScopeEditor(null)}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="admin-scope-editor-dialog">
          <DialogHeader><DialogTitle className="text-[#3F3F3F]">Périmètre d&apos;agences — {scopeEditor?.admin?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <MultiSelect
              options={agencyOptions}
              value={scopeEditor?.agency_ids || []}
              onChange={(v) => setScopeEditor({ ...scopeEditor, agency_ids: v })}
              placeholder="Toutes les agences (super-admin)"
              dataTestId="admin-scope-editor"
            />
            <div className="text-xs text-gray-600 bg-[#FAFAFA] border border-gray-200 p-3 space-y-1">
              <p><strong>Vide</strong> = super-admin (voit tout : candidats, offres, candidatures, stats globales).</p>
              <p><strong>Sélection</strong> = filtre tout le backoffice sur ces agences uniquement.</p>
              <p><strong>« Sans agence »</strong> = inclut aussi les candidats/offres non rattachés à une agence.</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={saveScope} disabled={savingScope} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="admin-scope-save">
                {savingScope ? "Enregistrement..." : "Enregistrer"}
              </Button>
              <Button variant="outline" onClick={() => setScopeEditor(null)} className="rounded-none">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
